import { DatePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TableHeaderComponent } from '@components/table-header/table-header.component';
import { CreateProductDialogComponent } from '@dialogs/create-product-dialog/create-product-dialog.component';
import { SelectionDialogComponent } from '@dialogs/selection-dialog/selection-dialog.component';
import {
  Campaign,
  GridOverrides,
  RowSelection,
  SUPPLIER_DATA_REQUEST_STATUS,
  SupplierDataRequestStatusLabel
} from '@mds/common';
import { MdsIcon } from '@mds/icons';
import {
  ButtonComponent,
  ButtonToggleComponent,
  GridCampaignStatusComponent,
  GridFloatingDateFilterComponent,
  GridFloatingTextFilterComponent,
  GridModule,
  GridNoRowsComponent,
  LoaderModule,
  TabComponent
} from '@mds/ui';
import { BulkActionComponent } from '@pages/information-requests/components/bulk-action/bulk-action.component';
import {
  INFORMATION_REQUESTS_TABS,
  InformationRequestTabValues
} from '@pages/information-requests/helpers/information-requests-tabs';
import { InformationRequestService } from '@pages/information-requests/information-request.service';
import { MenuService } from '@services/menu.service';
import { GridApi, IGetRowsParams, ITooltipParams, RowClickedEvent, ValueFormatterParams } from 'ag-grid-community';
import { ColDef } from 'ag-grid-enterprise';
import { isEqual } from 'lodash';
import moment from 'moment';

enum RequestSubTab {
  OPEN = 'open',
  CLOSED = 'closed'
}

const CLOSED_STATUSES = [SupplierDataRequestStatusLabel.DONE, SupplierDataRequestStatusLabel.NOT_RESPONDED];
const OPEN_STATUSES = [
  SupplierDataRequestStatusLabel.OPEN,
  SupplierDataRequestStatusLabel.REJECTED,
  SupplierDataRequestStatusLabel.SUBMITTED
];

@Component({
  selector: 'app-supplier-dashboard',
  standalone: true,
  imports: [ButtonComponent, GridModule, LoaderModule, TableHeaderComponent, ButtonToggleComponent, TabComponent],
  templateUrl: './supplier-dashboard.component.html',
  styleUrls: ['./supplier-dashboard.component.scss']
})
export class SupplierDashboardComponent implements OnInit {
  isAllowedToCreateRequests = false;
  showInviteColleague = false;
  loading: boolean;
  readonly icons = MdsIcon;
  outgoingOverriders: GridOverrides;
  incomingOverriders: GridOverrides;
  incomingGridApi: GridApi;
  outgoingGridApi: GridApi;
  paginationPageSize = 10;
  incomingRequestsCount = 0;
  outgoingRequestsCount = 0;
  pendingPCFCount = 0;
  protected name = 'Open Requests';
  protected selectedTab = InformationRequestTabValues.INCOMING;
  protected readonly tabs = INFORMATION_REQUESTS_TABS;
  protected readonly InformationRequestTabValues = InformationRequestTabValues;
  protected incomingSubTab = RequestSubTab.OPEN;
  protected outgoingSubTab = RequestSubTab.OPEN;
  protected readonly requestSubTabs = [
    { label: 'Open Requests', value: RequestSubTab.OPEN },
    { label: 'Closed Requests', value: RequestSubTab.CLOSED }
  ];

  private dialog = inject(MatDialog);
  private datePipe = inject(DatePipe);
  private informationRequestService = inject(InformationRequestService);
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  private router = inject(Router);

  ngOnInit(): void {
    this.checkInviteColleagueVisibility();
    this.getData();
    this.subscribeToMenuActions();
    this.checkUserPermission();
    this.hideMenus();
  }

  createRequestInformation() {
    const data = {
      app_name: 'Supplier Invitation',
      target: 'supplier/invite-colleague',
      tool: 'supplier/invite-colleague'
    };
    const dialogRef = this.dialog.open(CreateProductDialogComponent, {
      panelClass: 'create-new-dataset-modal',
      autoFocus: false,
      data: {
        dataCard: data
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      this.menuService.setReloadState(true);
      this.menuService.setReloadState(true);
    });
  }

  checkUserPermission() {
    const userInfo = JSON.parse(localStorage.getItem('user_info'));
    this.isAllowedToCreateRequests = !(userInfo?.features ?? [])
      .map((feature: { plan: string }) => feature.plan)
      .includes('external_supplier');
  }

  onIncomingGridReady(gridApi: GridApi) {
    this.incomingGridApi = gridApi;
    this.incomingGridApi?.setGridOption('datasource', this.createDatasource(false));
  }

  onOutgoingGridReady(gridApi: GridApi) {
    this.outgoingGridApi = gridApi;
    this.outgoingGridApi?.setGridOption('datasource', this.createDatasource(true));
  }

  createNewQuestionnaire() {
    const dialogRef = this.dialog.open(SelectionDialogComponent, {
      panelClass: 'importer-selection',
      data: {
        type: 'selection-dialog',
        header: 'Create new Questionnaire',
        url: 'customfields/search/questionnaire_type'
      }
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;

        this.loadQuestionnaire(result);
      });
  }

  onRowClicked(event: RowClickedEvent) {
    const { rid } = event.data;
    this.router.navigate(['/information-requests/request/', rid?.replace('#', '') ?? '']);
  }

  getBulkResponseOption() {
    const dialogRef = this.dialog.open(BulkActionComponent, {
      disableClose: true,
      data: {},
      height: '60vh',
      maxHeight: '400px'
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res.reload) {
          this.reloadGrids();
        }
      });
  }

  onSwitchView(event: MatButtonToggleChange) {
    this.selectedTab = event.value as InformationRequestTabValues;
  }

  onIncomingSubTabChange(tab: { label: string; value: RequestSubTab }) {
    this.incomingSubTab = tab.value;
    this.incomingOverriders = this.createOverrides(tab.value);
    if (this.incomingGridApi) {
      this.incomingGridApi.setGridOption('datasource', this.createDatasource(false));
    }
  }

  onOutgoingSubTabChange(tab: { label: string; value: RequestSubTab }) {
    this.outgoingSubTab = tab.value;
    this.outgoingOverriders = this.createOverrides(tab.value);
    if (this.outgoingGridApi) {
      this.outgoingGridApi.setGridOption('datasource', this.createDatasource(true));
    }
  }

  private createDatasource(isOutgoing: boolean) {
    return {
      getRows: (params: IGetRowsParams) => {
        this.incomingGridApi?.setGridOption('loading', true);
        this.outgoingGridApi?.setGridOption('loading', true);
        const page = Math.floor(params.startRow / this.paginationPageSize) + 1;
        const sorting =
          params.sortModel.length > 0
            ? {
                sort_order: params.sortModel[0].sort,
                column_name: params.sortModel[0].colId.replace('.value', '')
              }
            : null;
        let filters;

        const activeSubTab = isOutgoing ? this.outgoingSubTab : this.incomingSubTab;
        const gridApi = isOutgoing ? this.outgoingGridApi : this.incomingGridApi;
        const filterModel = gridApi?.getFilterModel();
        const hasGridStatusFilter = filterModel?.status !== null && filterModel?.status !== undefined;
        const statusFilter = hasGridStatusFilter
          ? {}
          : { status: activeSubTab === RequestSubTab.CLOSED ? CLOSED_STATUSES : OPEN_STATUSES };

        filters = [{ ...this.createFilterSchema(filterModel)[0], ...statusFilter }];

        const payload = { filters, sorting, page, limit: this.paginationPageSize };

        (isOutgoing
          ? this.informationRequestService.getOutgoingRequests(payload)
          : this.informationRequestService.getIncomingRequests(payload)
        ).subscribe(
          (data: { count: number; pending_pcf_count: number; items: Campaign[] }) => {
            if (isOutgoing) {
              this.outgoingGridApi?.hideOverlay();
              this.outgoingGridApi?.setGridOption('loading', false);
              if (data.count < 1) this.outgoingGridApi?.showNoRowsOverlay();
              this.outgoingRequestsCount = data.count;
              this.pendingPCFCount = 0;
            } else {
              this.incomingGridApi?.hideOverlay();
              this.incomingGridApi?.setGridOption('loading', false);
              if (data.count < 1) this.incomingGridApi?.showNoRowsOverlay();
              this.incomingRequestsCount = data.count;
              this.pendingPCFCount = data.pending_pcf_count || 0;
            }
            params.successCallback(data.items, data.count);
          },
          () => params.failCallback()
        );
      }
    };
  }

  private getData() {
    this.outgoingOverriders = this.createOverrides(this.outgoingSubTab);
    this.incomingOverriders = this.createOverrides(this.incomingSubTab);
    this.loading = false;
  }

  private subscribeToMenuActions() {
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reload: boolean) => {
      if (reload) {
        this.reloadGrids();
      }
    });
  }

  private reloadGrids() {
    if (this.incomingGridApi) {
      this.incomingGridApi.setGridOption('datasource', this.createDatasource(false));
    }
    if (this.outgoingGridApi) {
      this.outgoingGridApi.setGridOption('datasource', this.createDatasource(true));
    }
  }

  private createOverrides(subTab: RequestSubTab) {
    const hideFilterIcon = {
      suppressHeaderFilterButton: true,
      floatingFilterComponentParams: { suppressFilterButton: true }
    };
    const floatingTextFilterConfig: Partial<ColDef> = {
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      suppressFiltersToolPanel: true,
      floatingFilterComponent: GridFloatingTextFilterComponent,
      ...hideFilterIcon
    };
    const floatingDateFilterConfig: Partial<ColDef> = {
      filter: 'agDateColumnFilter',
      floatingFilter: true,
      suppressFiltersToolPanel: true,
      floatingFilterComponent: GridFloatingDateFilterComponent,
      ...hideFilterIcon
    };
    const allowedLabels = subTab === RequestSubTab.CLOSED ? CLOSED_STATUSES : OPEN_STATUSES;
    const statusOptions = SUPPLIER_DATA_REQUEST_STATUS.filter((s) => allowedLabels.includes(s.label));
    return {
      columnDefs: [
        {
          headerName: 'Name',
          field: 'name',
          minWidth: 250,
          headerTooltip: 'Name',
          tooltipField: 'name',
          ...floatingTextFilterConfig
        },
        {
          headerName: 'Status',
          field: 'status',
          minWidth: 150,
          headerTooltip: 'Status',
          cellRenderer: GridCampaignStatusComponent,
          filter: 'agSetColumnFilter',
          suppressHeaderFilterButton: true,
          filterParams: {
            values: statusOptions.map((statusOption) => statusOption.label),
            valueFormatter: (params) => {
              const matchedOption = statusOptions.find((option) => option.value === params.value);
              return matchedOption ? matchedOption.label : params.value;
            },
            comparator: (valueA, valueB) => {
              const firstOptionLabel = statusOptions.find((option) => option.value === valueA)?.label || valueA;
              const secondOptionLabel = statusOptions.find((option) => option.value === valueB)?.label || valueB;
              return firstOptionLabel.localeCompare(secondOptionLabel);
            },
            suppressSelectAll: true
          },
          ...hideFilterIcon
        },
        {
          headerName: 'Comments',
          field: 'comments_count',
          minWidth: 100,
          headerTooltip: 'Comments',
          tooltipField: 'comments_count',
          ...floatingTextFilterConfig
        },
        {
          headerName: 'Type',
          field: 'questionnaire_type',
          minWidth: 150,
          headerTooltip: 'Type',
          tooltipField: 'questionnaire_type',
          ...floatingTextFilterConfig
        },
        {
          headerName: 'End Date',
          field: 'requested_completion_date',
          minWidth: 200,
          headerTooltip: 'End Date',
          tooltipValueGetter: (params: ITooltipParams) => this.datePipe.transform(params.value, 'dd.MM.yyyy') ?? '',
          valueFormatter: (params: ValueFormatterParams) => this.datePipe.transform(params.value, 'dd.MM.yyyy') ?? '',
          ...floatingDateFilterConfig
        },
        {
          headerName: 'Site',
          field: 'site_name',
          minWidth: 150,
          headerTooltip: 'Site',
          tooltipField: 'site_name',
          ...floatingTextFilterConfig
        },
        {
          headerName: 'Company',
          field: 'company_name',
          minWidth: 150,
          headerTooltip: 'Company',
          tooltipField: 'company_name',
          ...floatingTextFilterConfig
        },
        {
          headerName: 'Requester',
          field: 'requestor_name',
          minWidth: 150,
          headerTooltip: 'Requester',
          tooltipField: 'requestor_name',
          ...floatingTextFilterConfig
        }
      ],
      defaultColDef: {
        flex: 1,
        minWidth: 50,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        filter: true,
        resizable: true,
        suppressMovable: true,
        floatingFilter: true
      },
      rowSelection: RowSelection.SINGLE,
      treeGrid: false,
      paginationAutoPageSize: false,
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      paginationPageSizeSelector: true,
      rowModelType: 'infinite',
      getRowStyle: (params) => (params.data?.rid ? { cursor: 'pointer' } : {}),
      noRowsOverlayComponent: GridNoRowsComponent,
      noRowsOverlayComponentParams: {
        title: 'No data to show the table',
        description: '',
        onClick: '',
        buttonLabel: ''
      }
    };
  }

  private loadQuestionnaire(card: { name: string; target: string }) {
    const dialogRef = this.dialog.open(CreateProductDialogComponent, {
      panelClass: 'create-new-dataset-modal',
      data: {
        dataCard: {
          app_name: card.name,
          tool: card.target,
          target: card.target,
          questionnaire: true
        }
      }
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (!data) return;
        this.dialog.closeAll();
        this.selectedTab = InformationRequestTabValues.OUTGOING;
        this.reloadGrids();
      });
  }

  private checkInviteColleagueVisibility(): void {
    this.informationRequestService.getInviteColleagueData().subscribe((res) => {
      this.showInviteColleague = Array.isArray(res) && res.length > 0;
    });
  }

  hideMenus() {
    this.menuService.updateMenuItem('back', { visible: false });
    this.menuService.updateMenuItem('submit', { visible: false });
    this.menuService.updateMenuItem('share', { visible: false });
  }

  createFilterSchema(filterModel) {
    const filters = {};
    Object.entries(filterModel || {}).forEach(([k, val]: any) => {
      if (val.filterType === 'text') {
        filters[k] = val.filter;
      } else if (val.filterType === 'date') {
        filters[k] = moment(val.dateFrom).format('YYYY-MM-DD');
      } else if (val.filterType === 'set') {
        filters[k] = val.values;
      }
    });
    return isEqual(filters, {}) ? [] : [filters];
  }
}
